import { siloIncentivesControllerAbi } from "@silo/contract-abi-types";
import {
  Address,
  AMOUNT_ZERO,
  ChainPublicClientService,
  makeAmount,
  ProtocolKeyService,
} from "@silo/types";
import { Array, Effect, Option, pipe } from "effect";
import { getContract } from "viem";
import { ContractsClientError } from "./types";
import { getProtocolConfig } from "@silo/config";
import { isNullable } from "effect/Predicate";

export type GetNativeRewardsBalance = ReturnType<
  typeof getNativeRewardsBalance
>;
export type GetNativeRewardsBalanceSuccess =
  Effect.Effect.Success<GetNativeRewardsBalance>;
export type GetNativeRewardsBalanceError =
  Effect.Effect.Error<GetNativeRewardsBalance>;
export type GetNativeRewardsBalanceContext =
  Effect.Effect.Context<GetNativeRewardsBalance>;

export function getNativeRewardsBalance(user: Address) {
  return Effect.gen(function* () {
    const protocolKey = yield* ProtocolKeyService;
    const { nativeRewards } = getProtocolConfig(protocolKey);

    if (isNullable(nativeRewards)) {
      return AMOUNT_ZERO;
    }

    const {
      historicalShareTokens,
      tokenAddress,
      siloIncentivesControllerAddress,
    } = nativeRewards;

    const { publicClient } = yield* ChainPublicClientService;

    const siloIncentivesControllerContract = getContract({
      address: siloIncentivesControllerAddress,
      abi: siloIncentivesControllerAbi,
      client: publicClient,
    });

    const shareTokens = pipe(
      historicalShareTokens[tokenAddress],
      Option.fromNullable,
      Option.map(Array.dedupe),
      Option.getOrElse((): Address[] => [])
    );

    const balance = yield* Effect.tryPromise({
      try: async () => {
        return siloIncentivesControllerContract.read.getRewardsBalance([
          shareTokens,
          user,
        ]);
      },
      catch: (cause) => new ContractsClientError({ cause }),
    });

    return makeAmount(balance);
  });
}
